import { InfoCircledIcon } from '@radix-ui/react-icons'
import * as Tooltip from '@radix-ui/react-tooltip'
import { TooltipWrapper } from 'components/TooltipWrapper'
import React from 'react'
import styled from 'styled-components'

import { TextCell } from './TextCell'

const TagContainer = styled.div<{
  $bgColor?: string
  $disabled?: boolean
  $textColor?: string
}>`
  padding: 2px 4px 2px 4px;
  border-radius: 4px;
  background-color: ${({ $bgColor, $disabled, theme }) =>
    $disabled ? 'transparent' : ($bgColor ?? theme.forecast.defaultButton)};
  color: ${({ $disabled, theme, $textColor }) =>
    $disabled
      ? theme.forecast.tertiaryText
      : ($textColor ?? theme.forecast.defaultText)};
  width: fit-content;
  max-width: 100%;
  min-height: 20px;
  min-width: 20px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.25rem;
  cursor: default;
`

const StyledTooltipContent = styled(Tooltip.Content)`
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  color: ${({ theme }) => theme.forecast.defaultText};
  border-radius: 4px;
  padding: 8px;
  z-index: 9999;
  background-color: ${({ theme }) => theme.forecast.glassBackground};
  backdrop-filter: blur(32px);
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.1),
    0px 4px 8px 0px rgba(0, 0, 0, 0.1);
`

const StyledTooltipArrow = styled(Tooltip.Arrow)`
  fill: ${({ theme }) => theme.forecast.defaultButton};
`

export const TagCell: React.FC<{
  text: string | number | undefined | React.ReactNode
  iconPosition?: 'left' | 'right'
  icon?: React.ReactNode
  color?: string
  tooltip?: React.ReactNode
  disabled?: boolean
  textColor?: string
  textStyle?: React.CSSProperties
}> = ({
  text,
  icon,
  iconPosition = 'right',
  tooltip,
  color,
  disabled,
  textColor,
  textStyle,
}) => {
  if (!text) return <TextCell />

  const effectiveIcon =
    icon ?? (tooltip ? <InfoCircledIcon width={'0.875rem'} /> : null)

  const tagBody = (
    <TagContainer $bgColor={color} $disabled={disabled} $textColor={textColor}>
      {iconPosition === 'left' ? effectiveIcon : null}
      <span style={textStyle}>{text}</span>
      {iconPosition === 'right' ? effectiveIcon : null}
    </TagContainer>
  )

  if (tooltip) {
    return (
      <Tooltip.Provider>
        <Tooltip.Root delayDuration={0}>
          <Tooltip.Trigger asChild>{tagBody}</Tooltip.Trigger>
          <StyledTooltipContent side='bottom' align='center'>
            {tooltip}
            <StyledTooltipArrow offset={5} />
          </StyledTooltipContent>
        </Tooltip.Root>
      </Tooltip.Provider>
    )
  }

  if (textStyle) {
    return (
      <TooltipWrapper side='top' offset={5} label={text}>
        {tagBody}
      </TooltipWrapper>
    )
  }

  return tagBody
}
